/**
 * FAQ content for the /faq page, grouped by topic.
 * Answers are also exposed to search engines / AI as FAQPage JSON-LD.
 */

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqCategory {
  id: string;
  title: string;
  icon: string;
  items: FaqItem[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: '🚀',
    items: [
      {
        question: 'What is DHH Collab?',
        answer:
          'DHH Collab is an online collaboration platform for Desi Hip-Hop. Producers upload beats, rappers record verses over them directly in the browser, and listeners discover and vote on tracks from across India — all in one place, completely free.',
      },
      {
        question: 'What are the different roles, and what can each one do?',
        answer:
          'There are four roles. Producers upload beats and set how many verses each beat can hold. Rappers browse beats and record verses over them. Producer-Rappers can do both. Listeners explore the community and vote on beats and verses. You pick your role when signing up.',
      },
      {
        question: 'Is DHH Collab free to use?',
        answer:
          'Yes. Creating an account, uploading beats, recording verses, and voting are all free.',
      },
      {
        question: "I signed up but can't log in. What should I do?",
        answer:
          'After signing up you need to activate your account using the link we email you. Check your inbox (and the spam/junk folder) for the activation email. If you never received it, open the activation page and use the "Resend email" option, or contact support.',
      },
    ],
  },
  {
    id: 'beats',
    title: 'Beats & Uploading',
    icon: '🎧',
    items: [
      {
        question: 'How do I upload a beat?',
        answer:
          'If your role is Producer or Producer-Rapper, click "Upload Beat" in the header. Give your beat a title, choose the audio file, set the verses limit (how many verses rappers can record on it), and publish. Your beat then appears on the home feed for rappers to discover.',
      },
      {
        question: 'Why does a beat have a verse count?',
        answer:
          'Each beat shows how many verses have been recorded on it versus its limit — for example "3 / 10". The limit is set by the producer when uploading (10 by default) and controls how many rappers can record on that beat. Once the limit is reached, no new verses can be added. Producers can raise or lower the limit on their own beats from the beat details page.',
      },
      {
        question: 'Can I delete a beat I uploaded?',
        answer:
          'Yes. Open your beat\'s details page and use the delete option. Note that deleting a beat also removes all verses recorded on it and their votes, so use it carefully — this cannot be undone.',
      },
    ],
  },
  {
    id: 'recording',
    title: 'Recording a Verse',
    icon: '🎤',
    items: [
      {
        question: 'How do I record a verse?',
        answer:
          'First, click on a beat card on the home page to open its details. If your role is Rapper or Producer-Rapper, you\'ll see a "Record Verse" button. Click it, complete the quick mic and speaker check, and hit "Start Recording" — the beat plays out loud while you rap along. When you stop, we mix your vocals with the beat, let you preview and fine-tune the result, and you can upload it or record again.',
      },
      {
        question: 'Why is there a microphone and speaker check before recording?',
        answer:
          'The check confirms your microphone can be accessed and your speakers or headphones can play the beat before you start a take. It catches permission and device problems upfront, so you don\'t lose a great performance to a silent mic.',
      },
      {
        question: 'How do I get the best recording quality?',
        answer:
          'Three things make the biggest difference: wear headphones (and tick the "I\'m wearing headphones" option) so the beat doesn\'t bleed into your mic, record in a quiet room, and watch the live mic level meter — green is good, and if it stays red you\'re too loud or too close to the mic.',
      },
      {
        question: 'What are the sliders shown after I stop recording?',
        answer:
          'After a take, we build a polished mix of your vocals and the beat. The sliders let you fine-tune it: "Vocal volume" and "Beat volume" balance the two against each other, and "Vocal timing" nudges your vocals earlier or later if they feel slightly off-beat. The preview updates as you adjust.',
      },
      {
        question: 'Can I listen to my verse before publishing it?',
        answer:
          'Yes. Nothing is published until you press "Upload Verse". Use "Play Recording" to listen as many times as you like, and "Record Again" to redo the take.',
      },
      {
        question: "Why can't I record on a certain beat?",
        answer:
          'Two common reasons: the beat has reached its verses limit (shown as, say, "10 / 10"), or your account role doesn\'t include recording — only Rappers and Producer-Rappers can record verses.',
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: '🔧',
    items: [
      {
        question: 'The microphone check fails even though I allowed access in the browser. Why?',
        answer:
          'This is almost always an operating-system level block, not the browser. On Windows, open Settings > Privacy & security > Microphone and make sure "Microphone access" and "Let desktop apps access your microphone" are both turned on. Also close other apps that may be holding the mic (Zoom, Teams, Discord), check that the right input device is set as default, then refresh the page and try again. On macOS, check System Settings > Privacy & Security > Microphone and enable your browser.',
      },
      {
        question: "I can't hear the beat during the speaker check.",
        answer:
          'Check your system volume and output device — a common culprit is audio routed to disconnected Bluetooth headphones. Pick the right output device in your system settings, then rerun the check.',
      },
      {
        question: 'My recorded verse sounds echoey or unclear.',
        answer:
          'This usually happens when the beat plays through speakers and re-enters your microphone. Wear headphones and tick the headphones option before recording. Also record in a quiet room, stay a hand\'s length from the mic, and use the volume sliders after the take to balance vocals against the beat.',
      },
      {
        question: "A beat or verse won't play.",
        answer:
          'First check your internet connection and refresh the page. If one specific track still fails, it may have an issue on our side — contact support with the beat or verse name and we\'ll look into it.',
      },
    ],
  },
  {
    id: 'community',
    title: 'Community & Profiles',
    icon: '🤝',
    items: [
      {
        question: 'How does voting work?',
        answer:
          'Users with the Listener role can vote on beats and verses they like. Votes help the best tracks surface for the whole community, and give producers and rappers feedback on their work.',
      },
      {
        question: 'How do I find other artists to collaborate with?',
        answer:
          'Use the "Find Artists" page in the header. You can search the community and filter by role and state to find producers or rappers near you.',
      },
      {
        question: 'How do I update my profile?',
        answer:
          'Open your profile from the header and use the edit option. You can update your username, state, and add your Instagram and YouTube links so collaborators can find your work.',
      },
      {
        question: 'I forgot my password. How do I reset it?',
        answer:
          'On the login page, click "Forgot password" and enter your email. We\'ll send you a link to set a new password. If the email doesn\'t arrive, check your spam folder.',
      },
    ],
  },
];

/** Flat list of every FAQ item (used for FAQPage structured data). */
export const ALL_FAQ_ITEMS: FaqItem[] = FAQ_CATEGORIES.flatMap(category => category.items);
